import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {AccessRequest} from '@/types/access-request'

export interface UseAccessRequestResult {
    request: AccessRequest | undefined
    isLoading: boolean
    error: Error | null
}

/**
 * Composite read hook for the AccessRequestDetail page (and the wizard in 607.09).
 *
 * Fetches the access request by id. The detail endpoint already returns the
 * full graph (items, approvers, approval chain, decisions), so no batch
 * follow-up queries are needed — the shape mirrors {@link useAutoAssignRuleRequest}
 * minus `linkedProfiles` / `linkedGroups` (access requests don't link to
 * standalone profile / function-group entities outside their items).
 */
export function useAccessRequest(id: string | undefined): UseAccessRequestResult {
    const services = useServices()

    const {data: request, isLoading, error} = useQuery({
        queryKey: ['access-request', id],
        queryFn: () => services.accessRequests.detail(id!),
        enabled: !!id,
    })

    return {
        request,
        isLoading,
        error: error as Error | null,
    }
}
